/**
 * This is a ProductDetail component. It will be used to display additional product infromation like ingredients used in product, restrictions, warning and instructions to use.'
 *
 * @module views/components/ProductDetail
 * @memberof -Common
 */
import './ProductDetail.scss';

import React, { useState } from 'react';

import PropTypes from 'prop-types';

import Accordion from '@ulta/core/components/Accordion/Accordion';
import Icon from '@ulta/core/components/Icon/Icon';
import Markdown from '@ulta/core/components/Markdown/Markdown';
import Text from '@ulta/core/components/Text/Text';

/**
 * Represents a ProductDetail organism component
 *
 * @method
 * @param {ProductDetailProps} props - React properties passed from composition
 * @returns ProductDetail
 */
const ProductDetail = React.forwardRef( ( props, ref ) => {
  const {
    description,
    descriptionAction,
    descriptionDataCaptureCollapseAction,
    id,
    ingredients,
    ingredientsAction,
    ingredientsDataCaptureCollapseAction,
    restrictions,
    restrictionsAction,
    restrictionsDataCaptureCollapseAction,
    usage,
    usageAction,
    usageDataCaptureCollapseAction,
    prop65WarningAction,
    prop65WarningDataCaptureCollapseAction,
    prop65WarningDisplay,
    prop65WarningDetail,
    prop65WarningLinkAction
  } = props;

  const [expandMore, setExpandMore] = useState( false );

  return (
    <div id={ id }
      className='ProductDetail'
      ref={ ref }
    >
      <div className='ProductDetail__Wrapper'>
        <div className='ProductDetail__Content'>
          { description && descriptionAction?.label &&
          (
            <Accordion title={ descriptionAction.label }
              titleTag='h3'
              expandAction={ descriptionAction }
              expandMore={ expandMore }
              setExpandMore={ setExpandMore }
              collapseAction={ descriptionDataCaptureCollapseAction }
            >
              <Markdown bodyStyle='body-2'
                cmsMarkdownContent={ description }
              />
            </Accordion> )
          }
          { usage && usageAction?.label &&
          <Accordion title={ usageAction.label }
            titleTag='h3'
            expandAction={ usageAction }
            expandMore={ expandMore }
            setExpandMore={ setExpandMore }
            collapseAction={ usageDataCaptureCollapseAction }
          >
            <Markdown bodyStyle='body-2'
              cmsMarkdownContent={ usage }
            />
          </Accordion>
          }
          { ingredients && ingredientsAction?.label &&
            <Accordion title={ ingredientsAction.label }
              titleTag='h3'
              expandAction={ ingredientsAction }
              expandMore={ expandMore }
              setExpandMore={ setExpandMore }
              collapseAction={ ingredientsDataCaptureCollapseAction }
            >
              <Markdown bodyStyle='body-2'
                cmsMarkdownContent={ ingredients }
              />
            </Accordion>
          }
          { prop65WarningDisplay && prop65WarningDetail && prop65WarningAction?.label &&
          <Accordion title={ prop65WarningAction.label }
            titleTag='h3'
            expandAction={ prop65WarningAction }
            expandMore={ expandMore }
            setExpandMore={ setExpandMore }
            collapseAction={ prop65WarningDataCaptureCollapseAction }
          >
            <div className='ProductDetail__Content__Prop65'>
              <Text
                htmlTag='p'
                textStyle='body-2'
              >
                <Icon size='m'
                  name='ExclamationTriangle'
                  className='ProductDetail__Content__Prop65--warningIcon'
                />
                { prop65WarningDetail }
              </Text>

            </div>
            <Text
              htmlTag='p'
              textStyle='body-2'
            >
              { prop65WarningLinkAction.label }
            </Text>
          </Accordion>
          }
          { restrictions && restrictionsAction?.label &&
          <Accordion title={ restrictionsAction.label }
            titleTag='h3'
            expandAction={ restrictionsAction }
            expandMore={ expandMore }
            setExpandMore={ setExpandMore }
            collapseAction={ restrictionsDataCaptureCollapseAction }
          >
            <Markdown bodyStyle='body-2'
              cmsMarkdownContent={ restrictions }
            />
          </Accordion>
          }
        </div>
      </div>
    </div>
  );
} );

/**
 * Property type definitions
 * @typedef ProductDetailProps
 * @type {object}
 * @property {string} description - Sets the description of the product
 * @property {object} descriptionAction - Sets the descriptionLabel of the product
 * @property {object} descriptionDataCaptureCollapseAction - Sets the description DataCapture Collapse Action
 * @property {string} id - Set the id of the product detail
 * @property {string} ingredients - Sets the ingredients of the product
 * @property {object} ingredientsAction - Sets the ingredientsLabel of the product
 * @property {object} ingredientsDataCaptureCollapseAction - Sets the ingredients DataCapture Collapse Action
 * @property {string} restrictions - Sets the restrictions of the product
 * @property {object} restrictionsAction - Sets the restrictionsLabel of the product
 * @property {object} restrictionsDataCaptureCollapseAction - Sets the restrictions DataCapture Collapse Action
 * @property {string} usage - Sets the usage of the product
 * @property {object} usageAction - Sets the usageLabel of the product
 * @property {object} usageDataCaptureCollapseAction - Sets the usage DataCapture Collapse Action
 * @property {boolean} prop65WarningDisplay - Flag to specify to display prop65WarningDetail string or not
 * @property {object} prop65WarningAction - Sets the prop65WarningLabel string
 * @property {object} prop65WarningDataCaptureCollapseAction - Sets the prop65Warning DataCapture Collapse Action
 * @property {string} prop65WarningDetail - Sets the prop65WarningDetail
 * @property {object} prop65WarningLinkAction - Sets the prop65WarningLinkAction
 */
export const propTypes = {
  description: PropTypes.string,
  descriptionAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  descriptionDataCaptureCollapseAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  /** This is the id for product detail */
  id: PropTypes.string,
  ingredients: PropTypes.string,
  ingredientsAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  ingredientsDataCaptureCollapseAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  restrictions: PropTypes.string,
  restrictionsAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  restrictionsDataCaptureCollapseAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  usage: PropTypes.string,
  usageAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  usageDataCaptureCollapseAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  prop65WarningDisplay: PropTypes.bool,
  prop65WarningAction: PropTypes.shape( {
    label: PropTypes.string
  } ),
  prop65WarningDetail: PropTypes.string,
  prop65WarningLinkAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  prop65WarningDataCaptureCollapseAction: PropTypes.shape( {
    label: PropTypes.string
  } )
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {boolean} prop65WarningDisplay=false - Flag to specify to display prop65WarningDetail string or not, default is false
 */
export const defaultProps = {
  prop65WarningDisplay: false
};

ProductDetail.propTypes = propTypes;
ProductDetail.defaultProps = defaultProps;
ProductDetail.displayName = 'ProductDetail';

export default ProductDetail;
